import React, { useEffect } from 'react';
import { FastField, Form, FormikProps } from 'formik';
import { Box, FormControl, Grid, Link } from '@mui/material';
import { TextField } from 'formik-material-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import routesConfig from 'constants/routesConfig.json';

import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'demoRegistration/styles/RegistrationCard.style';
import * as StyledHelp from 'auth/styles/LoginPage.style';
import * as Text from 'theme/components/Text.style';

import CompanySizeField from 'demoRegistration/components/customFields/CompanySizeField';
import IndustryField from 'demoRegistration/components/customFields/IndustryField';
import LanguageField from 'demoRegistration/components/customFields/LanguageField';
import Loader from 'eCoach/loaders/Loader';
import RegistrationError from 'demoRegistration/components/RegistrationError';
import RegistrationSuccess from 'demoRegistration/components/RegistrationSuccess';

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  language: string;
  industry: string;
  additional_infos: {
    user_specific_industry: string;
    job_title: string;
    experience_in_years: string;
    company_size: string;
    team_size: string;
    issues: string;
  };
}

interface RegistrationFormProps extends Partial<FormikProps<FormValues>> {
  isSubmitting: boolean;
  status?: {
    success?: boolean;
    error?: boolean;
  };
}

export default function RegistrationForm({
  isSubmitting,
  status,
  setFieldValue,
}: RegistrationFormProps): JSX.Element {
  const { t, i18n } = useTranslation('pageDemoRegistration');
  const history = useHistory();
  const [currentLanguage, setCurrentLanguage] = React.useState<string>(
    i18n.language
  );

  useEffect(() => {
    setFieldValue?.('language', currentLanguage);
  }, [setFieldValue, currentLanguage]);

  const handleLanguageChange = (value: string): void => {
    i18n.changeLanguage(value).then(() => {
      setFieldValue?.('language', value);
      setCurrentLanguage(value);
    });
  };

  if (status?.success) return <RegistrationSuccess t={t} />;
  if (status?.error) return <RegistrationError t={t} />;
  return (
    <>
      <Styled.HeaderBlock>
        <Text.H2>{t('Try it now!')}</Text.H2>
        <Text.P3>
          {t(
            'To tailor your demo experience, we would like to learn a bit more about you.'
          )}
        </Text.P3>
      </Styled.HeaderBlock>
      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name="first_name"
              label={t('First Name')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name="last_name"
              label={t('Last Name')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name="email"
              label={t('Email')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LanguageField
              key={currentLanguage}
              t={t}
              currentLanguage={currentLanguage}
              handleLanguageChange={handleLanguageChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <IndustryField
              key={currentLanguage}
              currentLanguage={currentLanguage}
              t={t}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name={`additional_infos.job_title`}
              label={t('Job Title')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name={`additional_infos.experience_in_years`}
              label={t('Experience In Years')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name={`additional_infos.company_name`}
              label={t('Company Name')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <CompanySizeField currentLanguage={currentLanguage} t={t} />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FastField
              key={currentLanguage}
              component={TextField}
              name="additional_infos.issues"
              label={t('Issues')}
              placeholder={t(
                'Team morale, interpersonal conflicts, difficult conversations, reaching set goals, etc.'
              )}
              multiline
              rows={4}
              fullWidth
            />
          </Grid>
          <Grid
            container
            flexDirection="column"
            alignItems="end"
            justifyContent="flex-end"
            sx={{ mt: 4 }}
          >
            <Buttons.Primary disabled={isSubmitting} type="submit">
              {!isSubmitting ? t('Submit') : t('Submitting... Please wait...')}
              {isSubmitting && <Loader inline />}
            </Buttons.Primary>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 4 }}
            >
              <Box sx={{ pt: 1, pl: 2 }}>
                <StyledHelp.HelpLink
                  href={routesConfig.FAQ_PAGE}
                  onClick={(e) => {
                    e.preventDefault();
                    history.push(routesConfig.FAQ_PAGE);
                  }}
                >
                  {t('Need help')}
                </StyledHelp.HelpLink>
              </Box>
              <Box sx={{ pt: 1, pl: 2 }}>
                <Text.P1>
                  <Trans
                    i18nKey="privacyPolicy"
                    components={[
                      <Link
                        key="0"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.nurau.com/privacy-policy"
                      />,
                    ]}
                    ns="pageDemoRegistration"
                  />
                </Text.P1>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Form>
    </>
  );
}
